import { useState, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { authService } from '../services/api';
import { queryKeys } from '../lib/react-query';
import type { User, LoginRequest, LoginResponse } from '../types/api';

interface UseAuthReturn {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (credentials: LoginRequest) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  error: string | null;
}

/**
 * Hardened auth hook:
 * - Single source of truth via React Query cache (auth/me).
 * - No extra /auth/me on mount if cached.
 * - Guarded by token presence to prevent duplicate calls.
 * - Mutations (login/logout) prime/clear the cache and token.
 */
export function useAuth(): UseAuthReturn {
  const qc = useQueryClient();
  const [mutationLoading, setMutationLoading] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  const hasToken = authService.isAuthenticated();

  const {
    data,
    isLoading: meLoading,
    error: meError,
    refetch,
  } = useQuery<User, any>({
    queryKey: queryKeys.auth.me,
    queryFn: () => authService.getCurrentUser(),
    enabled: hasToken,
    staleTime: 5 * 60 * 1000,
    retry: (failureCount, error: any) => {
      const status = error?.response?.status ?? error?.status;
      if (status === 401 || 403 === status) return false;
      return failureCount < 1;
    },
  });

  const login = useCallback(async (credentials: LoginRequest) => {
    setMutationLoading(true);
    setLocalError(null);
    try {
      const response: LoginResponse = await authService.login(credentials);
      authService.setToken(response.access_token);

      // Prime the cache to avoid an immediate extra fetch
      qc.setQueryData<User>(queryKeys.auth.me, response.user);
      
      // Invalider toutes les requêtes pour rafraîchir les données après connexion
      console.log('🔄 [AUTH] Connexion réussie, rafraîchissement du cache...');
      await qc.invalidateQueries();
    } catch (err: any) {
      setLocalError(err?.message || 'Erreur lors de la connexion');
      throw err;
    } finally {
      setMutationLoading(false);
    }
  }, [qc]);

  const logout = useCallback(async () => {
    setMutationLoading(true);
    setLocalError(null);
    try {
      await authService.logout();
    } catch {
      // ignore network/logout error; token cleared by service
    } finally {
      // Clear cached user and any auth-related state
      qc.removeQueries({ queryKey: queryKeys.auth.me });
      qc.clear(); // Clear all queries to ensure complete logout
      setMutationLoading(false);
      
      console.log('🔄 [AUTH] Déconnexion, nettoyage du cache...');
      
      // Force redirect to login page
      window.location.href = '/login';
    }
  }, [qc]);

  const refreshUser = useCallback(async () => {
    setLocalError(null);
    const result = await refetch();
    if (result.error) {
      throw result.error as any;
    }
  }, [refetch]);

  const error =
    localError ??
    (meError ? (meError as any).message ?? 'Erreur lors de la vérification de l’authentification' : null);

  return {
    user: data ?? null,
    isLoading: meLoading || mutationLoading,
    isAuthenticated: !!data,
    login,
    logout,
    refreshUser,
    error,
  };
}